import { t } from "@travily/i18n";
import { Select } from "@ui/components/Select";
import { Wizard } from "@ui/components/Wizard";
import type { ReactNode } from "react";
import { CurrencyChangeConfirmModal } from "@/components/CurrencyChangeConfirmModal/CurrencyChangeConfirmModal";

const COLOR = "green" as const;

type SupportedCountry = { code: string; name: string; currency: string };

export interface ProductSellingCountryStepViewProps {
  /** Wizard progress: 1-based index of the current stage. */
  currentStage: number;
  /** Wizard progress: total number of stages. */
  totalStages: number;
  /** Navigate to the previous stage. Omit to hide the back button. */
  onBack?: () => void;
  /** Top-bar tertiary actions (e.g. "Questions?", "Finish later"). */
  topBarActions: ReactNode;
  /** Advance to / submit the current stage (may open the reprice confirm modal). */
  onPrimary: () => void;
  /** Whether the primary button shows a pending spinner. */
  primaryPending: boolean;
  /** Field error for the country picker, or undefined when valid. */
  countryError?: string;
  /** Currently-selected country code (empty string when none picked). */
  country: string;
  /** Called with the new country code ("" when cleared) on selection change. */
  onCountryChange: (value: string) => void;
  /** Countries the seller can pick from, each with its payout currency. */
  supportedCountries: SupportedCountry[];
  /** Currency of the picked country; undefined when no country is selected. */
  selectedCurrency?: string;
  /** The seller's current selling currency. */
  currentCurrency: string;
  /** How many existing paid drafts a currency change would re-price. */
  repriceableCount: number;
  /** Whether the currency-change confirm modal is open. */
  currencyConfirmOpen: boolean;
  /** Open/close the currency-change confirm modal (dismiss reverts the picker). */
  onCurrencyConfirmOpenChange: (open: boolean) => void;
  /** Confirm the currency change and continue. */
  onCurrencyConfirm: () => void;
}

// Asked once for a paid seller who has not told us where they get paid; sets the
// account country -> currency, framed as banking/payout, never taxes. The confirm-
// before-reprice flow is wired by the container (shared with services).
export function ProductSellingCountryStepView({
  currentStage,
  totalStages,
  onBack,
  topBarActions,
  onPrimary,
  primaryPending,
  countryError,
  country,
  onCountryChange,
  supportedCountries,
  selectedCurrency,
  currentCurrency,
  repriceableCount,
  currencyConfirmOpen,
  onCurrencyConfirmOpenChange,
  onCurrencyConfirm,
}: ProductSellingCountryStepViewProps) {
  return (
    <>
      <Wizard color={COLOR} currentStage={currentStage} totalStages={totalStages} onBack={onBack}>
        {topBarActions}
        <Wizard.PrimaryButton onPress={onPrimary} isPending={primaryPending}>
          {t("common.actions.continue")}
        </Wizard.PrimaryButton>
        <Wizard.Stage
          heading={t("web.products.creator.steps.selling_country.show.title")}
          helper={t("web.products.creator.steps.selling_country.show.subtitle")}
        >
          <div className="w-full max-w-[600px] text-left">
            <Select
              variant="wizard"
              isLabelHidden
              label={t("web.products.creator.steps.selling_country.show.country_label")}
              placeholder={t("web.products.creator.steps.selling_country.show.country_placeholder")}
              selectedKey={country || null}
              onSelectionChange={(key) => onCountryChange(key ? String(key) : "")}
              isInvalid={Boolean(countryError)}
              errorMessage={countryError}
            >
              {supportedCountries.map((entry) => (
                <Select.Item key={entry.code} id={entry.code} textValue={entry.name}>
                  {entry.name}
                </Select.Item>
              ))}
            </Select>
            {selectedCurrency ? (
              <p className="mt-4 rounded-xl bg-green-50 px-4 py-3 text-green-900 text-sm">
                {t("web.products.creator.steps.selling_country.show.currency_hint", { currency: selectedCurrency })}
              </p>
            ) : null}
          </div>
        </Wizard.Stage>
      </Wizard>
      <CurrencyChangeConfirmModal
        isOpen={currencyConfirmOpen}
        onOpenChange={onCurrencyConfirmOpenChange}
        onConfirm={onCurrencyConfirm}
        isPending={primaryPending}
        count={repriceableCount}
        fromCurrency={currentCurrency}
        toCurrency={selectedCurrency ?? currentCurrency}
      />
    </>
  );
}
